import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Rating, IconButton, Snackbar, Alert,
} from '@mui/material';
import {
  Users, CalendarCheck, PawPrint, HandHelping, QrCode,
  Eye, Trash2, ChevronRight, ArrowRightLeft, X,
} from 'lucide-react';
import { format } from 'date-fns';
import api from '../config/api';

const ff = "'Poppins', sans-serif";

// ─── Stat Card ────────────────────────────────────────────────
const StatCard = ({ title, subtitle, value, icon, color, loading, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: '1 1 220px',
      minWidth: 200,
      p: 2.5,
      borderRadius: '14px',
      background: '#FAFBFC',
      border: '1px solid #EDF2F7',
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': { borderColor: color, boxShadow: `0 4px 16px ${color}18` },
    }}
  >
    <Box sx={{
      width: 40, height: 40, borderRadius: '10px',
      background: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
    }}>
      {icon}
    </Box>
    <Box sx={{ fontFamily: ff, fontSize: 12, fontWeight: 500, color: '#A0AEC0', mb: 0.25 }}>{subtitle}</Box>
    <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 600, color: '#2D3748', mb: 1 }}>{title}</Box>
    {loading ? (
      <Skeleton width={60} height={28} />
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ fontFamily: ff, fontSize: 24, fontWeight: 700, color: '#1A202C' }}>
          {(value || 0).toLocaleString()}
        </Box>
        <Box sx={{
          width: 32, height: 32, borderRadius: '8px', background: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid #EDF2F7',
        }}>
          <ChevronRight size={16} color="#A0AEC0" />
        </Box>
      </Box>
    )}
  </Box>
);

// ─── Feedback Card ────────────────────────────────────────────
const FeedbackCard = ({ review, onView, onDelete }) => (
  <Box sx={{
    minWidth: 380, p: 2.5, background: 'white', borderRadius: '12px',
    border: '1px solid #EDF2F7', flexShrink: 0,
    transition: 'border-color 0.2s', '&:hover': { borderColor: '#CBD5E0' },
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
      <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 600, color: '#1A202C' }}>
        {review?.user?.fullName || review?.author || 'Anonymous'}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <IconButton size="small" onClick={() => onView?.(review)} sx={{ width: 28, height: 28 }}>
          <Eye size={14} color="#718096" />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete?.(review)} sx={{ width: 28, height: 28 }}>
          <Trash2 size={14} color="#E53E3E" />
        </IconButton>
      </Box>
    </Box>
    <Box sx={{ fontFamily: ff, fontSize: 12.5, color: '#718096', mb: 1.5, lineHeight: 1.6,
      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
    }}>
      "{review?.comment || review?.text || 'No comment provided'}"
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>
        {review?.createdAt ? format(new Date(review.createdAt), 'MMM dd, yyyy') : review?.date || ''}
      </Box>
      <Rating value={review?.rating || 5} readOnly size="small" sx={{ fontSize: 14 }} />
    </Box>
  </Box>
);

// ─── Transaction Card ─────────────────────────────────────────
const TransactionCard = ({ txn }) => (
  <Box sx={{
    p: 2, borderRadius: '10px', border: '1px solid #EDF2F7',
    transition: 'border-color 0.2s', '&:hover': { borderColor: '#CBD5E0' },
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%', background: '#EBF8FF',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ArrowRightLeft size={14} color="#32A6D8" />
        </Box>
        <Box>
          <Box sx={{ fontFamily: ff, fontSize: 12.5, fontWeight: 600, color: '#2D3748' }}>
            {txn?.type || 'Coin Transfer'}
          </Box>
          <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>
            {txn?.user?.fullName || 'User'}
          </Box>
        </Box>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 700, color: txn?.amount > 0 ? '#38A169' : '#E53E3E' }}>
          {txn?.amount > 0 ? '+' : ''}{txn?.amount || 0}
        </Box>
        <Box sx={{ fontFamily: ff, fontSize: 10, color: '#A0AEC0' }}>
          {txn?.createdAt ? format(new Date(txn.createdAt), 'MMM dd, HH:mm') : ''}
        </Box>
      </Box>
    </Box>
  </Box>
);

// ─── Booking Card ─────────────────────────────────────────────
const BookingCard = ({ booking }) => {
  const statusColors = {
    COMPLETED: { bg: '#F0FFF4', color: '#38A169' },
    ONGOING: { bg: '#FFFFF0', color: '#D69E2E' },
    PENDING: { bg: '#EBF8FF', color: '#3182CE' },
    CONFIRMED: { bg: '#EBF8FF', color: '#32A6D8' },
    CANCELLED: { bg: '#FFF5F5', color: '#E53E3E' },
  };
  const status = booking?.status || 'COMPLETED';
  const s = statusColors[status] || statusColors.COMPLETED;

  return (
    <Box sx={{
      p: 2, borderRadius: '10px', border: '1px solid #EDF2F7',
      transition: 'border-color 0.2s', '&:hover': { borderColor: '#CBD5E0' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 600, color: '#2D3748' }}>
            {booking?.serviceType || 'Boarding'}
          </Box>
          <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>
            {booking?.user?.fullName || booking?.sitter?.fullName || 'User'}
            {booking?.startDate ? ` - ${format(new Date(booking.startDate), 'MMM dd')}` : ''}
            {booking?.endDate ? ` to ${format(new Date(booking.endDate), 'MMM dd')}` : ''}
          </Box>
        </Box>
        <Box sx={{
          px: 1.5, py: 0.25, borderRadius: '20px', background: s.bg,
          fontFamily: ff, fontSize: 10, fontWeight: 600, color: s.color,
        }}>
          {status.charAt(0) + status.slice(1).toLowerCase()}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>
          Total: {booking?.totalAmount ? `$${booking.totalAmount}` : 'N/A'}
        </Box>
        {booking?.pet && (
          <Box sx={{ fontFamily: ff, fontSize: 11, color: '#718096' }}>
            {booking.pet.name} ({booking.pet.type})
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Section Header ───────────────────────────────────────────
const SectionHeader = ({ title, onViewAll }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
    <Box sx={{ fontFamily: ff, fontSize: 16, fontWeight: 600, color: '#1A202C' }}>{title}</Box>
    {onViewAll && (
      <Box
        onClick={onViewAll}
        sx={{
          fontFamily: ff, fontSize: 12, fontWeight: 500, color: '#32A6D8',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5,
          '&:hover': { color: '#005A64' },
        }}
      >
        View All <ChevronRight size={14} />
      </Box>
    )}
  </Box>
);

// ═══════ DASHBOARD ═══════════════════════════════════════════════
export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [reviews, setReviews] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewReview, setViewReview] = useState(null);
  const [deleteReview, setDeleteReview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const statsRes = await api.get('/admin/dashboard/stats');
      setStats(statsRes.data.data || statsRes.data);
      const [revRes, txnRes, bookRes] = await Promise.allSettled([
        api.get('/admin/reviews?limit=4'),
        api.get('/admin/transactions?limit=5'),
        api.get('/admin/bookings?limit=4'),
      ]);
      if (revRes.status === 'fulfilled') setReviews(revRes.value?.data?.data?.reviews || []);
      if (txnRes.status === 'fulfilled') setTransactions(txnRes.value?.data?.data?.transactions || []);
      if (bookRes.status === 'fulfilled') setBookings(bookRes.value?.data?.data?.bookings || []);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!deleteReview) return;
    try {
      await api.delete(`/admin/reviews/${deleteReview.id}`);
      setSnackbar({ open: true, message: 'Review deleted', severity: 'success' });
      setDeleteReview(null);
      fetchData();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' });
    }
  };

  const statCards = [
    { title: 'Total Users', subtitle: 'All registered', value: stats.totalUsers, icon: <Users size={18} color="#32A6D8" />, color: '#32A6D8', path: '/users' },
    { title: 'Total Bookings', subtitle: 'All time', value: stats.totalBookings, icon: <CalendarCheck size={18} color="#38A169" />, color: '#38A169', path: '/bookings' },
    { title: 'Total Pets', subtitle: 'Registered pets', value: stats.totalPets, icon: <PawPrint size={18} color="#D69E2E" />, color: '#D69E2E', path: '/pets' },
    { title: 'Services', subtitle: 'Total setups', value: stats.totalServices, icon: <HandHelping size={18} color="#805AD5" />, color: '#805AD5', path: '/services' },
  ];

  return (
    <Box>
      {/* Stat Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        {statCards.map((c, i) => <StatCard key={i} {...c} loading={loading} onClick={() => navigate(c.path)} />)}
      </Box>

      {/* Reviews */}
      <Box sx={{ mb: 4 }}>
        <SectionHeader title="Recent Reviews" onViewAll={() => navigate('/reviews')} />
        <Box sx={{
          display: 'flex', gap: 2, overflowX: 'auto', pb: 1,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { background: '#E2E8F0', borderRadius: 4 },
        }}>
          {loading
            ? [1, 2, 3].map(i => <Skeleton key={i} variant="rounded" width={380} height={110} sx={{ borderRadius: '12px', flexShrink: 0 }} />)
            : reviews.length > 0
              ? reviews.map((r, i) => <FeedbackCard key={r.id || i} review={r} onView={setViewReview} onDelete={setDeleteReview} />)
              : <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0', py: 3 }}>No reviews yet.</Box>
          }
        </Box>
      </Box>

      {/* Bottom: Transactions | Bookings | QR */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionHeader title="Recent Transactions" onViewAll={() => navigate('/transactions')} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {loading
              ? [1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: '10px' }} />)
              : transactions.length > 0
                ? transactions.map((t, i) => <TransactionCard key={t.id || i} txn={t} />)
                : <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0', py: 3 }}>No transactions yet.</Box>
            }
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionHeader title="Recent Bookings" onViewAll={() => navigate('/bookings')} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {loading
              ? [1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={65} sx={{ borderRadius: '10px' }} />)
              : bookings.length > 0
                ? bookings.map((b, i) => <BookingCard key={b.id || i} booking={b} />)
                : <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0', py: 3 }}>No bookings yet.</Box>
            }
          </Box>
        </Box>

        {/* QR Card */}
        <Box sx={{
          width: { xs: '100%', lg: 220 }, flexShrink: 0,
          p: 2.5, borderRadius: '14px',
          background: 'linear-gradient(145deg, #32A6D8 0%, #005A64 100%)',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          minHeight: 280, cursor: 'pointer',
        }} onClick={() => navigate('/linked-qr')}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px', background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <QrCode size={20} color="white" />
          </Box>
          <Box>
            <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 600, color: 'white', mb: 0.25 }}>QR Codes</Box>
            <Box sx={{ fontFamily: ff, fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Linked to pets</Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {loading
              ? <Skeleton width={50} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.15)' }} />
              : <Box sx={{ fontFamily: ff, fontSize: 22, fontWeight: 700, color: 'white' }}>{(stats.totalPets || 0).toLocaleString()}</Box>
            }
            <Box sx={{
              px: 1.5, py: 0.5, borderRadius: '6px', background: 'rgba(255,255,255,0.2)',
              fontFamily: ff, fontSize: 10, fontWeight: 600, color: 'white',
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              View <ChevronRight size={12} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Review Detail Dialog */}
      <Dialog open={!!viewReview} onClose={() => setViewReview(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: ff, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          Review Details
          <IconButton size="small" onClick={() => setViewReview(null)}><X size={18} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {viewReview && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Rating value={viewReview.rating || 5} readOnly size="small" />
                <Box sx={{ fontFamily: ff, fontSize: 13, color: '#718096' }}>{viewReview.rating}/5</Box>
              </Box>
              <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 600, color: '#2D3748', mb: 0.5 }}>
                {viewReview.user?.fullName || 'Anonymous'}
              </Box>
              <Box sx={{ fontFamily: ff, fontSize: 12, color: '#A0AEC0', mb: 2 }}>
                {viewReview.createdAt ? format(new Date(viewReview.createdAt), 'MMMM dd, yyyy') : ''}
              </Box>
              <Box sx={{ fontFamily: ff, fontSize: 13, color: '#4A5568', lineHeight: 1.7, p: 2, background: '#F7FAFC', borderRadius: '8px' }}>
                {viewReview.comment || 'No comment provided.'}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewReview(null)} sx={{ fontFamily: ff, textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteReview} onClose={() => setDeleteReview(null)} maxWidth="xs">
        <DialogTitle sx={{ fontFamily: ff, fontWeight: 600 }}>Delete Review?</DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: ff, fontSize: 13, color: '#718096' }}>
            This will permanently remove the review by {deleteReview?.user?.fullName || 'this user'}.
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteReview(null)} sx={{ fontFamily: ff, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDeleteReview} variant="contained" color="error" sx={{ fontFamily: ff, textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ fontFamily: ff }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
